import type { CategoryContent } from './row.js';

export const SCULPTURE: CategoryContent = {
  bscba: [
    [
      'Michelangelo\'s David was carved from a single block of which stone?',
      [
        'Marble',
        'Granite',
        'Limestone',
        'Sandstone',
      ],
      0,
      'Michelangelo carved David from a block of white Carrara marble between 1501–1504, establishing marble as the gold standard for Renaissance sculpture.',
    ],
    [
      'What is the primary advantage of lost-wax casting over direct stone carving?',
      [
        'Details refined in wax before casting',
        'It produces larger sculptures than carving',
        'It requires no kiln or furnace',
        'It cannot crack or weather over time',
      ],
      0,
      'Lost-wax (cire perdue) lets sculptors work in pliable wax, refine every detail, then cast the final bronze—allowing corrections impossible when carving irreversibly into stone.',
    ],
    [
      'Which of these is an example of subtractive sculpture?',
      [
        'Carving a figure from a marble block',
        'Building up clay in layers to form a head',
        'Pouring molten bronze into a mold',
        'Welding metal sheets together',
      ],
      0,
      'Subtractive sculpture removes material to reveal form—chiseling marble or stone. Additive builds up; casting and welding also add material.',
    ],
    [
      'Auguste Rodin\'s The Thinker was originally conceived as part of which larger work?',
      [
        'The Gates of Hell',
        'The Burghers of Calais',
        'Monument to Balzac',
        'The Kiss',
      ],
      0,
      'The Thinker began as a small figure representing Dante Alighieri contemplating the souls below, part of Rodin\'s monumental The Gates of Hell commission.',
    ],
    [
      'What is the primary characteristic of relief sculpture?',
      [
        'Figures project from a flat background surface',
        'It is carved entirely from a single stone without a backing',
        'It depicts multiple subjects in the round on separate columns',
        'It requires an interior hollow core for stability',
      ],
      0,
      'Relief sculpture shows forms raised or recessed from a flat background plane—the depth can vary from shallow (bas-relief) to deeply carved (high relief).',
    ],
    [
      'Bronze is favored for casting sculpture because of which property?',
      [
        'It resists oxidation once patinated',
        'It remains warm to the touch indefinitely',
        'It becomes harder when exposed to sunlight',
        'It weighs less than equivalent marble sculptures',
      ],
      0,
      'Bronze melts at ~1085°C (low enough for manageable casting), captures fine details, and forms a protective patina layer that resists further corrosion.',
    ],
    [
      'Which sculptural technique uses a mold to produce a replica from an original form?',
      [
        'Casting',
        'Modeling',
        'Carving',
        'Assembly',
      ],
      0,
      'Casting pours liquid material (usually molten metal or plaster) into a mold of the original, solidifying to create a replica or editions of the work.',
    ],
    [
      'Gian Lorenzo Bernini\'s David differs from Michelangelo\'s primarily in its',
      [
        'a dynamic, twisting pose of action',
        'material—bronze rather than marble',
        'smaller scale—it is a miniature sculpture',
        'classical, symmetrical frontal orientation',
      ],
      0,
      'Bernini\'s David (1623–24) captures the athlete mid-action, arm drawn back, body twisted—Baroque dynamism contrasts with Renaissance repose in Michelangelo\'s version.',
    ],
    [
      'What material did ancient Greek sculptors favor for larger free-standing figures?',
      [
        'Bronze and marble',
        'Terracotta and wood',
        'Limestone and clay',
        'Sandstone alone',
      ],
      0,
      'Classical Greeks cast large bronzes (now rare due to melting for reuse) and carved marble for public monuments, temples, and sanctuaries.',
    ],
    [
      'Rodin\'s The Burghers of Calais is notable for',
      [
        'ordinary citizens depicted as heroic subjects',
        'being carved entirely from a single marble block',
        'representing a biblical narrative',
        'its use of gilded bronze to catch light',
      ],
      0,
      'The Burghers breaks convention by making six medieval townspeople the protagonists—full-scale, realistic, dignified but not beautified, addressing civic duty.',
    ],
    [
      'Which process removes air bubbles from clay before hand-modeling?',
      [
        'Wedging',
        'Burnishing',
        'Patinating',
        'Tempering',
      ],
      0,
      'Wedging—repeatedly cutting and folding clay—expels trapped air that would otherwise cause the sculpture to crack or explode when fired.',
    ],
    [
      'What architectural element often features sculptural relief panels?',
      [
        'Temple friezes',
        'Window hinges',
        'Interior door frames',
        'Roof shingles',
      ],
      0,
      'Friezes are continuous horizontal bands; metopes are individual panels between triglyphs. Both decorated Greek temples with mythological and narrative reliefs.',
    ],
    [
      'Constantine Brancusi\'s sculptures are characterized by',
      [
        'essential, abstract geometric forms',
        'extreme realism and photographic detail',
        'heavy use of colorful glazed tiles',
        'complex interior mechanisms',
      ],
      0,
      'Brancusi stripped away detail toward pure form and movement, pioneering modernist abstraction in sculpture and influencing minimalism.',
    ],
    [
      'Which material requires firing in a kiln to achieve permanence?',
      [
        'Terracotta',
        'Plaster',
        'Clay slip',
        'Modeling wax',
      ],
      0,
      'Unfired terracotta clay becomes brittle and fragile; kiln firing at high temperature hardens it irreversibly into durable ceramic.',
    ],
    [
      'What is the Elgin Marbles\' original source?',
      [
        'The Parthenon in Athens',
        'Egyptian temples at Thebes',
        'Roman Forum sculptures',
        'Minoan palace ruins on Crete',
      ],
      0,
      'The Elgin Marbles (5th century BCE) are marble sculptures and friezes from the Parthenon, removed by Thomas Bruce in the early 1800s.',
    ],
  ],
  msc: [
    [
      'What distinguishes the direct carving method pioneered by Modernists from Renaissance blocking-out techniques?',
      [
        'Direct carving works without preliminary models',
        'It requires three years minimum training in granite',
        'Stone is first painted with guidelines before any chisel work',
        'A plaster cast must be made before touching the final block',
      ],
      0,
      'Direct carving (Brancusi, Henry Moore) eschews preparatory drawings and models, allowing stone properties to shape artistic outcome—contrasting with Renaissance planning-to-execution.',
    ],
    [
      'The term "contrapposto" in sculpture refers to',
      [
        'Weight shifted to one leg, creating asymmetry',
        'the technique of painting bronze with metallic accents',
        'the practice of carving figures in pairs facing each other',
        'the orientation of a sculpture toward true north',
      ],
      0,
      'Contrapposto (Italian "against-pose") places weight on one leg, tilting hips and shoulders—a humanizing technique the Renaissance adopted from Hellenistic statuary.',
    ],
    [
      'Which ancient culture is credited with developing the lost-wax casting technique to high refinement?',
      [
        'Etruscan and Roman',
        'Middle Kingdom Egyptian',
        'Mycenaean Greek',
        'Shang Dynasty Chinese',
      ],
      0,
      'Etruscans refined lost-wax (cire perdue) to exceptional detail; Romans adopted and systematized it for large-scale bronzes, documented by Pliny the Elder.',
    ],
    [
      'Barbara Hepworth\'s pierced, abstract forms represent a departure from figural sculpture because they',
      [
        'treat the void as equal to mass',
        'are purely decorative rather than monumental',
        'must be viewed from a single fixed angle',
        'use only salvaged industrial materials',
      ],
      0,
      'Hepworth\'s holes and voids are not merely hollows but integral forms—viewers walk through and around, engaging space itself as sculptural material.',
    ],
    [
      'What is "modeling" in sculptural practice?',
      [
        'Building up material in clay or wax',
        'Making a plaster mold of a finished work',
        'Photographing the sculpture from multiple angles',
        'Polishing the surface to a high sheen',
      ],
      0,
      'Modeling builds form upward—clay, wax, or plaster added incrementally. Distinct from carving (subtractive) and casting (mold-based).',
    ],
    [
      'Donatello\'s bronze David (c. 1440) was revolutionary because',
      [
        'the first large male nude bronze since antiquity',
        'it depicted a child rather than an adult hero',
        'the figure was positioned recumbent rather than upright',
        'it incorporated colored glass inlays',
      ],
      0,
      'Donatello\'s David revived the Classical contrapposto nude—first monumental bronze male nude since Rome, initiating Renaissance revival of antique ideals.',
    ],
    [
      'The "Elgin Marbles" controversy centers on the legal status of sculptural fragments from which structure?',
      [
        'The Parthenon on the Acropolis of Athens',
        'The Temple of Jupiter Capitolinus in Rome',
        'The Mausoleum at Halicarnassus',
        'The Library of Alexandria colonnades',
      ],
      0,
      'The Parthenon sculptures (5E marbles) were removed by the British Museum in the early 1800s; Greece argues for repatriation, Britain for universal access.',
    ],
    [
      'Which material property is essential to achieve a true "white patina" finish on ancient marble sculptures?',
      [
        'Its crystalline structure scatters light',
        'Repeated applications of mineral oil',
        'Grinding the surface smooth at 5000-grit fineness',
        'Exposure to saltwater for decades',
      ],
      0,
      'White marble\'s translucency and fine crystalline grain scatter light evenly. The "glow" comes from subsurface scattering, not surface coating.',
    ],
    [
      'Gian Lorenzo Bernini\'s approach to sculpture differed from Renaissance ideals in his emphasis on',
      [
        'theatrical drama and dynamic movement',
        'purely symmetrical frontal orientation',
        'matte surface finishes avoiding polishing',
        'small chamber-scale works only',
      ],
      0,
      'Bernini pursued Baroque excess—spiraling forms, exuberant drapery, sensual surfaces, and movement—rejecting Renaissance restraint for emotional impact.',
    ],
    [
      'What artistic principle guided Neoclassical sculptors like Antonio Canova?',
      [
        'Emulation of Greek and Roman ideals',
        'Rejection of all ancient precedent in favor of original invention',
        'Exclusive use of abstract, non-representational forms',
        'Incorporation of mechanical moving parts and gears',
      ],
      0,
      'Neoclassical sculptors (Canova, Thorvaldsen) revived antique subjects, proportional ideals, and restrained elegance against Baroque excess.',
    ],
    [
      'The technique of "chasing" in bronze sculpture refers to',
      [
        'finishing cast metal with hammer and chisel',
        'coating the bronze with a chemical patina solution',
        'welding separate cast pieces together',
        'painting linear designs onto the finished surface',
      ],
      0,
      'Chasing uses specialized hammers and burins to sharpen edges, texture surfaces, and refine casting imperfections after the bronze has cooled.',
    ],
    [
      'Henry Moore\'s family of sculptures emphasizes the formal relationship between',
      [
        'abstract form and landscape',
        'color theory and emotional symbolism',
        'mechanical precision and industrial repetition',
        'bright surfaces and dark interior cavities',
      ],
      0,
      'Moore\'s reclining figures and mother-child groups explore organic abstraction, relating the body to geology and landscape through monumental forms.',
    ],
    [
      'Which sculptural movement explicitly rejected fine-art marble and bronze in favor of industrial and everyday materials?',
      [
        'Constructivism',
        'Neoclassicism',
        'Classical Greek',
        'Victorian academic sculpture',
      ],
      0,
      'Constructivists (Tatlin, Gabo) and Arte Povera artists used steel, concrete, plastic, rope—challenging the precious-material ideal.',
    ],
    [
      'What does the term "edition" mean in the context of casting multiple copies?',
      [
        'A numbered series of castings from the same mold',
        'The original plaster model before any casting begins',
        'A sculpture painted in different color variations',
        'A temporary cast destroyed after exhibition',
      ],
      0,
      'Editions (e.g., "2 of 8") establish scarcity and authenticity—foundries and artists control how many authorized casts are made from a mold.',
    ],
    [
      'Tilman Riemenschneider\'s late Gothic wooden sculptures are prized for their',
      [
        'intricate undercutting and flowing drapery',
        'use of bright enamel paints and gilt decoration',
        'abstract geometric simplification',
        'life-size scale and human anatomical accuracy',
      ],
      0,
      'Riemenschneider\'s wood carvings feature densely carved drapery with deep undercuts casting sharp shadows, epitomizing late Gothic linear expression.',
    ],
  ],
  phd: [
    [
      'The rediscovery of Egyptian block statue conventions influenced Modernist abstraction through which key sculptural principle?',
      [
        'Compact, cubic forms that compress the figure',
        'The exclusive use of limestone as a material',
        'The requirement that all sculpture face eastward',
        'The prohibition against depicting individual identity',
      ],
      0,
      'Brancusi, Henry Moore, and others adopted the Egyptian block statue\'s reduction of the body into essential geometric volumes, enabling formal abstraction.',
    ],
    [
      'In the lost-wax process, what structural problem necessitates the "core" and "pins" (or "sprues")?',
      [
        'They stop the mold from shifting',
        'They are purely decorative and optional',
        'They allow water drainage after casting',
        'They serve as attachment points for gilding',
      ],
      0,
      'The ceramic core holds the internal mold cavity in position; pins (sprues) anchor it, ensuring even metal flow and preventing cavity collapse during pouring.',
    ],
    [
      'The Klassizismus movement of early 19th-century Scandinavia established which aesthetic principle in direct reaction to Napoleonic excess?',
      [
        'Greek Revival austerity and geometric clarity',
        'Baroque emotional exuberance and dynamic movement',
        'Medieval revival and Gothic ornamentation',
        'Oriental and non-Western formal sources',
      ],
      0,
      'Scandinavian Classicism (Thorvaldsen, Bissen) channeled political anxiety through austere Greek forms, equating restraint with Enlightenment values.',
    ],
    [
      'What conservation challenge do bronze sculptures face at the cellular level when exposed to the "bronze disease"?',
      [
        'Cuprous chloride corrosion weakens the metal',
        'The patina fades and loses color',
        'The surface becomes too smooth for grip',
        'Internal mold cores expand and crack the bronze',
      ],
      0,
      'Bronze disease (cuprous chloride) is an active corrosion; if moisture reactivates dormant chlorides, they spread internally, potentially destroying the object.',
    ],
    [
      'How did the Expressionist sculptors of early 20th-century Germany diverge from their Rodin-influenced predecessors?',
      [
        'Distorted proportion and raw surface texture',
        'They abandoned bronze entirely in favor of stone',
        'They rejected all representational imagery for pure abstraction',
        'They returned to Renaissance ideals of beauty',
      ],
      0,
      'German Expressionists (Barlach, Marcks) twisted anatomy, roughened surfaces, and amplified psychological intensity—using Rodin\'s form-freedom against his humanism.',
    ],
    [
      'The technique of "troubadour," practiced by later medieval and Renaissance sculptors, involved',
      [
        'faces carved in high relief, body left sketch-like',
        'painting elaborate costumes onto finished marble figures',
        'creating hollow bronze shells for ceremonial processions',
        'embedding semi-precious stones into marble to suggest jewelry',
      ],
      0,
      'Troubadour heightened portrait faces while leaving drapery and body summary—prioritizing likeness and identity over complete formal development.',
    ],
    [
      'In the early Renaissance, how did the artist\'s "cartoon" function in relation to sculptural execution?',
      [
        'A full-scale drawing transferred to guide carving',
        'It was a humorous sketch unrelated to the finished work',
        'It replaced the need for any preliminary models',
        'It was applied as decorative paint atop the finished sculpture',
      ],
      0,
      'Renaissance cartoons (scaled drawings) were transferred onto stone using pouncing or chalk dust; they guided the blocking-out phase before detailed carving.',
    ],
    [
      'How did the development of iron and steel sculpture in the 20th century challenge the material hierarchy of sculpture?',
      [
        'Iron\'s strength allowed open, cantilevered forms',
        'Steel became more expensive than bronze, elevating its status',
        'Iron\'s susceptibility to rust made it unsuitable for outdoor work',
        'Welding required sculptors to be trained as engineers first',
      ],
      0,
      'Steel\'s strength and weldability enabled pioneers (González, Smith) to construct open-air space rather than carve or model mass—a conceptual revolution.',
    ],
    [
      'What did the "truth to materials" principle articulate in Modernist sculptural theory?',
      [
        'Each material\'s properties should guide its form',
        'All materials are functionally equivalent under proper finishing',
        'The most expensive material always produces the best sculpture',
        'Sculptures should conceal what material they are made from',
      ],
      0,
      'Truth to materials (Hepworth, Moore, Brancusi) rejected artifice: marble should reveal grain, bronze its casting process, wood its grain—exposing material integrity.',
    ],
    [
      'Which Hellenistic sculptural work exemplified "pathos" formalism through compressed, dynamic musculature and psychological intensity?',
      [
        'Laocoön\'s twisted, anguished bodies',
        'The Nike of Samothrace\'s serene uplifted pose',
        'The Venus de Milo\'s classical contrapposto',
        'The Doryphoros\'s idealized athletic youth',
      ],
      0,
      'Laocoön and his sons—serpent-coiled, writhing—epitomized Hellenistic drama: anatomical distortion and anguished expression heightened emotional impact.',
    ],
    [
      'How did Renaissance anatomists\' influence on sculptors alter the representation of the human body compared to medieval conventions?',
      [
        'Precise anatomical knowledge replaced spiritual idealization',
        'Medieval figures became more abstract and less naturalistic',
        'Anatomical study was forbidden by the Church',
        'Renaissance sculptors ignored the body entirely for abstract forms',
      ],
      0,
      'Renaissance study of cadavers and anatomy (Leonardo, Michelangelo) produced convincing musculature, foreshortening, and dynamic pose—replacing medieval stylization.',
    ],
    [
      'What role did the "substitution mold" technique play in accelerating Renaissance bronze production?',
      [
        'Separate molds per body part, cast and assembled',
        'It allowed sculptors to carve faster with mechanical tools',
        'It eliminated the need for a foundry furnace',
        'It was purely decorative with no practical function',
      ],
      0,
      'Piece-molding allowed sections (head, arms, torso, legs) to be cast separately and welded together—speeding production and enabling variations.',
    ],
    [
      'In what way did the Barbizon sculptors\' engagement with landscape realism influence 19th-century outdoor public monuments?',
      [
        'They anchored figures in naturalistic terrain',
        'They rejected all figurative sculpture for landscapes only',
        'They eliminated the human figure entirely from public art',
        'They required monuments to be carved from local stone exclusively',
      ],
      0,
      'Barbizon sculptors (Mène, Fremiet) depicted animals and figures in credible landscape settings, grounding public monuments in natural rather than ideal space.',
    ],
    [
      'How does the "ground plane" function as a compositional element in Henry Moore\'s abstract sculptures?',
      [
        'An active field shaping form and negative space',
        'It is purely functional to prevent tipping',
        'Moore always elevated sculptures on tall pedestals',
        'The ground plane is irrelevant to his work\'s meaning',
      ],
      0,
      'Moore engaged the ground deliberately: figures nestle into or reach above it, making the earth itself part of the formal and conceptual composition.',
    ],
    [
      'What conservation principle applies to the structural stabilization of ancient marble sculptures that have fractured along grain lines?',
      [
        'Reversible adhesives, weaker than the stone',
        'Any gap must be permanently filled with colored epoxy',
        'Fractured marbles cannot be repaired',
        'Modern concrete is the best bonding agent',
      ],
      0,
      'Reversibility is cardinal: adhesives must release without damage if future conservation requires intervention, and must not transfer stress to sound marble.',
    ],
  ],
  professor: [
    [
      'The "paragone" argument of Renaissance sculptors asserted the superiority of three-dimensional form over painting based on which epistemological premise?',
      [
        'Its physical solidity and spatial presence',
        'Sculpture was more expensive to produce than painting',
        'Painting had no tradition in antiquity',
        'Marble was the only morally pure artistic medium',
      ],
      0,
      'Renaissance paragone championed sculpture\'s tangible presence and spatial engagement against painting\'s illusionism—a hierarchy rooted in Plato\'s forms and Renaissance materialism.',
    ],
    [
      'How did Michelangelo\'s theological conception of the sculptor\'s role—"liberating" the figure from the stone—reflect Neoplatonic idealism?',
      [
        'The form already existed in the stone, waiting to be revealed',
        'Michelangelo believed sculpture was purely mechanical labor',
        'He rejected all philosophical frameworks in favor of empirical trial',
        'Stone was dead matter that required animation through painting',
      ],
      0,
      'Michelangelo framed carving as disclosure, not creation—echoing Plotinus: the ideal form dwells in potential within matter; the artist releases it through vision and skill.',
    ],
    [
      'What methodological innovation did Rodin introduce to public monument practice that shifted sculptural meaning from static commemoration to psychological temporality?',
      [
        'Fragmentary, unfinished forms conveyed psychological flux',
        'Photographic documentation of the sculpting process',
        'The exclusive use of classical idealized forms',
        'Rejection of all public commissions in favor of private galleries',
      ],
      0,
      'Rodin\'s fragmented limbs, gestural incompleteness, and rough surfaces introduced psychological expressiveness and temporal consciousness—modernizing the monument.',
    ],
    [
      'How did Brancusi\'s engagement with non-Western sculptural traditions (particularly Romanian folk and African) inform his theory of "essential form"?',
      [
        'Form emerges from material nature, not imposed proportion',
        'African sculpture has no formal value and Brancusi ignored it',
        'Non-Western art was purely decorative with no conceptual depth',
        'Brancusi converted these traditions to Western academic standards',
      ],
      0,
      'Brancusi cited folk carving and African work as proof that formal power emerged from respect for material and spiritual intuition—not Renaissance proportion systems.',
    ],
    [
      'In what sense did Donald Judd\'s rejection of metaphor and symbolic reference in Minimalist sculpture represent an epistemological break from Modernism?',
      [
        'The object was a literal, phenomenological presence, not symbolic',
        'Judd\'s work contained elaborate symbolic meaning like all previous sculpture',
        'Minimalism was the final flowering of Romantic idealism',
        'Judd sought to resurrect classical allegory',
      ],
      0,
      'Judd\'s literalism rejected the Modernist assumption that form expressed an inner essence; the cube is a cube—encountered directly, not metaphorically.',
    ],
    [
      'How did the rise of industrial bronze foundries in 19th-century France, particularly the Barbédienne and Susse Frères houses, restructure the artist-craftsman relationship in sculpture?',
      [
        'Licensed maquette-to-edition foundry production',
        'Foundries eliminated bronze casting entirely',
        'Industrial production had no impact on sculptural practice',
        'Bronze casting became less skilled and more mechanical',
      ],
      0,
      'Barbédienne and Susse established edition systems, licensing, quality control, and reproducibility—commodifying sculpture while fragmenting artist-maker identity.',
    ],
    [
      'What does the archaeological concept of "recarving" in ancient marble sculpture reveal about continuities of meaning-making and reuse across Roman and Christian periods?',
      [
        'Pagan statues were recarved with Christian iconography',
        'Recarving is purely accidental damage',
        'Ancient sculptors made no changes after initial completion',
        'Christian artisans had no knowledge of classical technique',
      ],
      0,
      'Recarving demonstrates active cultural appropriation: portraits became saints, surfaces were resculpted to align with new theology—meaning is palimpsest, not monolithic.',
    ],
    [
      'How did the "dematerialization" thesis of Land Art and Conceptual sculptors reconfigure the material object\'s ontological status within sculpture?',
      [
        'Ephemeral works prioritized concept over the object',
        'Material became more important than ever',
        'Conceptual art eliminated all physical presence',
        'This movement returned sculpture to classical marble carving',
      ],
      0,
      'Spiral Jetty, land reclamations, and photograph-based works asked: is the art the physical intervention, the documentation, the concept, or the viewer\'s encounter?',
    ],
    [
      'In the context of feminist interventions in sculpture, how did artists like Magdalena Abakanowicz address the body and seriality to critique heroic monument traditions?',
      [
        'Repetitive, anonymous bodies replaced heroic figures',
        'They returned to idealized classical nudes',
        'Feminist sculpture rejected all repetition and mass production',
        'Their work had no connection to political critique',
      ],
      0,
      'Abakanowicz\'s identical headless torsos in rows evoke both individuality-in-mass and state uniformity—subverting the monument\'s celebration of unique greatness.',
    ],
    [
      'What theoretical implications does the conservation debate surrounding patina removal on ancient bronzes hold regarding the relationship between time, authenticity, and artistic intent?',
      [
        'Cleaning to bare metal erases the object\'s history',
        'All ancient bronze should be polished to look new',
        'Patina has no historical or aesthetic significance',
        'Conservation should aim to reverse all time-based changes',
      ],
      0,
      'Modern conservation ethics reject "pristine restoration"—patina records age, weathering, and cultural layers; removing it imposes false originality and erases temporal evidence.',
    ],
    [
      'How do lattice and jali carving traditions in Indian temple sculpture function as both structural and spiritual systems that complicate Western distinctions between ornament and form?',
      [
        'Pierced screens serve visual, acoustic, devotional roles',
        'These carvings are merely decorative with no deeper significance',
        'Indian sculpture has no connection to spiritual intent',
        'Lattice carving is structurally weak and impractical',
      ],
      0,
      'Jali transforms the void into a permeable membrane: light filters, sight multiplies, the sacred permeates space—collapsing ornament-form hierarchy.',
    ],
    [
      'What does the critical reassessment of Art Deco sculpture reveal about the historiographic exclusion of "decorative" or "applied" sculpture from Modernist canons?',
      [
        'Deco\'s fusion of fine art and industrial design',
        'Art Deco was purely commercial with no artistic merit',
        'Modernism embraced all decorative traditions equally',
        'Deco sculpture is indistinguishable from mass production toys',
      ],
      0,
      'Art Deco\'s hybrid status—bridging fine and applied arts—revealed Modernism\'s anxieties about commodification, leisure, and the boundaries of "art" itself.',
    ],
    [
      'How do the restoration controversies surrounding the Parthenon sculptures and the Elgin Marbles implicate debates about cultural ownership, colonial extraction, and the ethics of universal museums?',
      [
        'Retention appeals to "global access" mask imperial appropriation',
        'There is no ethical issue with removing sculptures from their original sites',
        'Cultural origin has no bearing on where objects should be housed',
        'Universal museums have always been neutral repositories',
      ],
      0,
      'The Parthenon sculptures\' case study: colonial removals are legitimized retroactively by "preservation" and "access" rhetoric—obscuring theft and context-erasure.',
    ],
    [
      'What methodological challenges does the attribution of medieval sculpted figural details present when stylistic hands, workshop practices, and documentary records are sparse or contradictory?',
      [
        'Attribution rests on stylometric and archival analysis',
        'Medieval sculptures always come with clear artist signatures',
        'Attribution is never necessary for historical understanding',
        'All medieval work is anonymous and intentionally so',
      ],
      0,
      'Medieval sculpture resists singular authorship: workshop training, collective carving, patron control, and anonymous documentation mean attribution is speculative.',
    ],
    [
      'How do contemporary artists employing 3D scanning, digital modeling, and algorithmic design challenge the phenomenological and embodied epistemologies central to Modernist sculpture theory?',
      [
        'Digital processes replace material intuition',
        'Digital sculpture is identical to hand carving',
        'New technologies have not changed sculptural practice',
        'Algorithmic design restores Romantic individualism',
      ],
      0,
      'CAD-to-fabrication systems displace embodied knowledge with algorithmic decision-making; the question becomes whether the sculptor scripts code or matter.',
    ],
  ],
};
